import { Box, SelectChangeEvent, Theme, Typography } from "@mui/material";
import { TableHeaderData } from "../../types";
import React from "react";
import { PerformanceChartData, PerformersObj, PieChartData } from "../types";
import { Performance } from "./Performance";
import CustomTable from "../../common/customTable";
import DataTable from "./DataTable";
import PieChartComponent from "./PieChartComponent";
import { BottomFivePerformersLoading, TopFivePerformersLoading } from "../LoadingComponents";
import { NO_DATA_ERROR_MESSAGE } from "../../../../utils/constant";

const dropdownOptions = [
    {
        label: "Market Capitalization",
        value: '1'
    },
    {
        label: "Sector Allocation",
        value: '2'
    },

]

const performersTableHeaderData: TableHeaderData[] = [
    {
        id: "schemeName",
        label: "SECURITY NAME",
        align: "left",
        isSortable: false
    },
    {
        id: "category",
        label: "CATEGORY",
        align: "center",
        isSortable: false
    },
    {
        id: "fundHouse",
        label: "ISSUER",
        align: "center",
        isSortable: false
    },
    {
        id: "return",
        label: "Return",
        align: "center",
        isSortable: false
    },
    {
        id: "exposure",
        label: "EXPOSURE (%)",
        align: "center",
        isSortable: false
    },
]

const currentDate = new Date();
const cutoffDate = (n: number) => new Date(currentDate.getFullYear() - n, 0, 1);

interface DirectEquityOverviewProps {
    isLoading: boolean;
    selectedDropdownOption: string;
    setSelectedDropdownOption: React.Dispatch<React.SetStateAction<string>>;
    fetchPieChartData: (currentValue: string) => void;
    setActivePerformanceChartButton: React.Dispatch<React.SetStateAction<string>>;
    setFilteredPerformanceData: React.Dispatch<React.SetStateAction<PerformanceChartData>>;
    performanceData: PerformanceChartData;
    isPieChartDataLoading: boolean;
    pieChartData: PieChartData;
    isPerformanceLoading: boolean;
    filteredPerformanceData: PerformanceChartData;
    activePerformanceChartButton: string;
    isTopFivePerformersDataLoading: boolean;
    topFivePerformersData: PerformersObj;
    isBottomFivePerformersDataLoading: boolean;
    bottomFivePerformersData: PerformersObj
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export default function DirectEquityOverview({
    isLoading,
    selectedDropdownOption,
    setSelectedDropdownOption,
    fetchPieChartData,
    setActivePerformanceChartButton,
    setFilteredPerformanceData,
    performanceData,
    isPieChartDataLoading,
    pieChartData,
    isPerformanceLoading,
    filteredPerformanceData,
    activePerformanceChartButton,
    isTopFivePerformersDataLoading,
    topFivePerformersData,
    isBottomFivePerformersDataLoading,
    bottomFivePerformersData,
    open,
    setOpen
}: DirectEquityOverviewProps): JSX.Element {

    const handleDropdownValueChange = async (event: SelectChangeEvent) => {
        setSelectedDropdownOption(event.target.value)

        await fetchPieChartData(event.target.value)
    }

    const handlePerformanceChartButtonClick = (currentButton: string) => {
        setActivePerformanceChartButton(currentButton);

        if (currentButton === '6M') {
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

            setFilteredPerformanceData({
                ...performanceData,
                data1: performanceData?.data1.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
                data2: performanceData?.data2.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
                data3: performanceData?.data3.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
                data4: performanceData?.data4.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
                data5: performanceData?.data5.filter(([timestamp, _]) => timestamp >= sixMonthsAgo.getTime()),
            })
        } else if (currentButton === '1Y') {
            setFilteredPerformanceData({
                ...performanceData,
                data1: performanceData?.data1.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
                data2: performanceData?.data2.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
                data3: performanceData?.data3.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
                data4: performanceData?.data4.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
                data5: performanceData?.data5.filter(([timestamp, _]) => timestamp >= cutoffDate(1).getTime()),
            })
        } else if (currentButton === 'Inception') {
            setFilteredPerformanceData(performanceData)
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "15px" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "25px" },
                    rowGap: { xs: "15px", lg: "0px" }
                }}
            >
                <Box
                    sx={(theme: Theme) => ({
                        flex: { xs: 1, lg: 2 },
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "25px 30px 45px 30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        border: `3px solid ${theme.palette.stroke.main}`,
                    })}
                >
                    <PieChartComponent
                        isLoading={isPieChartDataLoading || isLoading}
                        selectedDropdownOption={selectedDropdownOption}
                        handleDropdownValueChange={handleDropdownValueChange}
                        dropdownOptions={dropdownOptions}
                        pieChartData={pieChartData.data}
                        pieChartLabelData={pieChartData.labelData}
                        pieChartTotalValue={pieChartData.totalValue}
                        isSuccess={pieChartData.isSuccess}
                        open={open}
                        setOpen={setOpen}
                    />

                </Box>
                <Box
                    sx={(theme: Theme) => ({
                        flex: { xs: 1, lg: 3 },
                        borderRadius: "25px",
                        padding: "25px 30px 30px 30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        border: `3px solid ${theme.palette.stroke.main}`,
                    })}
                >
                    <Performance
                        isLoading={isPerformanceLoading || isLoading}
                        isSuccess={filteredPerformanceData.isSuccess}
                        data={filteredPerformanceData}
                        activeButton={activePerformanceChartButton}
                        onButtonClick={handlePerformanceChartButtonClick}
                        key={activePerformanceChartButton}
                    />
                </Box>
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    borderRadius: "25px",
                    padding: "35px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    border: `3px solid ${theme.palette.stroke.main}`,
                })}

            >
                {(isTopFivePerformersDataLoading || isLoading) && (<TopFivePerformersLoading />)}
                {(!(isTopFivePerformersDataLoading || isLoading) && !topFivePerformersData.isSuccess) && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                paddingBottom: "20px"
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({
                                    lineHeight: '25px',
                                    letterSpacing: "0.01px",
                                    color: theme.palette.text.primary,
                                })}
                            >
                                Top 5 Performers
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                variant="body_600"
                                sx={(theme: Theme) => ({
                                    fontWeight: 500,
                                    lineHeight: "22.5px",
                                    letterSpacing: "0.01px",
                                    color: theme.palette.text.primary,
                                    textAlign: "center",
                                    width: "100%"
                                })}
                            >
                                {NO_DATA_ERROR_MESSAGE}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {(!(isTopFivePerformersDataLoading || isLoading) && topFivePerformersData.isSuccess) && (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            Top 5 Performers
                        </Typography>
                        <Box sx={{ marginTop: "20px" }}>
                            <CustomTable
                                isLoading={false}
                                tableBody={
                                    <DataTable data={topFivePerformersData.data} />
                                }
                                tableData={topFivePerformersData.data}
                                tableHeaderData={performersTableHeaderData}
                                isPaginationVisible={false}
                                tableDataCount={topFivePerformersData.data.length}
                                tableBodyLoaderRowCustomStyles={{
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                tableHeadRowCustomStyles={{
                                    '& th:first-of-type': {
                                        paddingLeft: "0px"
                                    }
                                }}
                                tableBodyCustomStyles={{
                                    '& tr:last-child td': {
                                        border: 0
                                    }
                                }}
                                loadingRowsNum={5}
                            />
                        </Box>
                    </>
                )}
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    borderRadius: "25px",
                    padding: "35px",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    border: `3px solid ${theme.palette.stroke.main}`,
                })}
            >
                {(isBottomFivePerformersDataLoading || isLoading) && (<BottomFivePerformersLoading />)}
                {(!(isBottomFivePerformersDataLoading || isLoading) && !bottomFivePerformersData.isSuccess) && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                paddingBottom: "20px"
                            }}
                        >
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({
                                    lineHeight: '25px',
                                    letterSpacing: "0.01px",
                                    color: theme.palette.text.primary,
                                })}
                            >
                                Bottom 5 Performers
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                variant="body_600"
                                sx={(theme: Theme) => ({
                                    fontWeight: 500,
                                    lineHeight: "22.5px",
                                    letterSpacing: "0.01px",
                                    color: theme.palette.text.primary,
                                    textAlign: "center",
                                    width: "100%"
                                })}
                            >
                                {NO_DATA_ERROR_MESSAGE}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {(!(isBottomFivePerformersDataLoading || isLoading) && bottomFivePerformersData.isSuccess) && (
                    <>
                        <Typography
                            variant="h2"
                            sx={
                                (theme: Theme) => ({
                                    lineHeight: "25px",
                                    letterSpacing: "0.01px",
                                    color: theme.palette.text.primary
                                })
                            }
                        >
                            Bottom 5 Performers
                        </Typography>
                        <Box sx={{ marginTop: "20px" }}>
                            <CustomTable
                                isLoading={false}
                                tableBody={
                                    <DataTable data={bottomFivePerformersData.data} />
                                }
                                tableData={bottomFivePerformersData.data}
                                tableHeaderData={performersTableHeaderData}
                                isPaginationVisible={false}
                                tableDataCount={bottomFivePerformersData.data.length}
                                tableBodyLoaderRowCustomStyles={{
                                    '& td:first-of-type': {
                                        paddingLeft: "0px"
                                    },
                                }}
                                tableHeadRowCustomStyles={{
                                    '& th:first-of-type': {
                                        paddingLeft: "0px"
                                    }
                                }}
                                tableBodyCustomStyles={{
                                    '& tr:last-child td': {
                                        border: 0
                                    }
                                }}
                                loadingRowsNum={5}
                            />
                        </Box>
                    </>
                )}
            </Box >
        </Box >
    )
}